import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import CompleteBatchDialog from '../components/lab/CompleteBatchDialog';
import PhaseKey from '../components/lab/PhaseKey';
import { useConfirm } from '../components/ConfirmDialog';
import { getBatch, setBatchStatus, updateBatchProgress } from '../lib/labData';
import { formatBatchG, formatGrams, formatPct, containerCount, yieldPercent } from '../lib/lab';
import { formatTempWithAlt } from '../lib/instructions';
import { groupByPhase } from '../lib/formulaModel';
import './Lab.css';

const DATE = new Intl.DateTimeFormat('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
const MONTH = new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric' });

const STATUS_LABEL = {
  planned: 'Planned',
  in_progress: 'In progress',
  completed: 'Completed',
  on_hold: 'On hold',
  abandoned: 'Abandoned',
};

/* The screen that's open on the counter while you make the thing.
 *
 * Everything it shows comes from the batch's own snapshot, never from the
 * formula — so a sheet opened next year shows what that batch actually used.
 */
export default function BatchPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [batch, setBatch] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);

  const load = useCallback(async () => {
    try {
      setBatch(await getBatch(id));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <p className="formula-loading">Loading…</p>;
  if (error) return <p className="formula-error">Couldn't load this batch: {error}</p>;
  if (!batch) return <p className="formula-error">That batch no longer exists.</p>;

  const lines = batch.resolved_lines || [];
  const method = batch.method_snapshot || {};
  const temps = (method.temperatures || []).filter((t) => t?.label && t.low != null);
  const steps = method.steps || [];
  const lineDone = batch.line_progress || [];
  const stepDone = batch.step_progress || [];
  const done = batch.status === 'completed';
  const planned = containerCount(batch.containers);

  const toggleLine = async (i) => {
    const next = lineDone.includes(i) ? lineDone.filter((x) => x !== i) : [...lineDone, i];
    setBatch((b) => ({ ...b, line_progress: next }));
    await updateBatchProgress(batch.id, { lineProgress: next });
  };

  const toggleStep = async (i) => {
    const next = stepDone.includes(i) ? stepDone.filter((x) => x !== i) : [...stepDone, i];
    setBatch((b) => ({ ...b, step_progress: next }));
    await updateBatchProgress(batch.id, { stepProgress: next });
  };

  const changeStatus = async (status, question) => {
    if (question && !(await confirm(question))) return;
    const updated = await setBatchStatus(batch.id, status);
    setBatch(updated);
  };

  // The first unchecked step: what to do next, highlighted so you can find
  // your place after looking away.
  const currentStep = steps.findIndex((_, i) => !stepDone.includes(i));

  return (
    <>
      <div className="lab-crumb">
        <Link to="/production">← Production</Link>
      </div>

      <header className="batch-head">
        <div>
          <h1 className="batch-head-code">{batch.batch_code}</h1>
          <p className="batch-head-sub">
            {batch.formula?.family?.name}
            {batch.variant?.name ? ` — ${batch.variant.name}` : ''} · v{batch.formula?.version_number}
          </p>
        </div>
        <div className="batch-head-right">
          <span className={'lab-pill status-' + batch.status}>{STATUS_LABEL[batch.status]}</span>
          <span className="batch-head-size">
            {formatBatchG(batch.planned_batch_g ?? batch.target_batch_g)}
          </span>
        </div>
      </header>

      {/* Pinned: the thing checked most often, so it never scrolls away. */}
      {temps.length > 0 && (
        <div className="batch-temps">
          {temps.map((t, i) => (
            <span className="batch-temp-chip" key={i}>
              {t.label}{' '}
              <strong>
                {formatTempWithAlt({ label: t.label, value: t.low, max: t.high, unit: t.unit })}
              </strong>
            </span>
          ))}
        </div>
      )}

      <div className="batch-body">
        <section className="panel lab-card">
          <div className="panel-head">
            <span className="panel-label">Ingredients</span>
            {!done && (
              <span className="lab-row-meta">
                {lineDone.length} of {lines.length} weighed
              </span>
            )}
          </div>

          {lines.length === 0 ? (
            <p className="formula-muted">
              This batch was logged before batch sheets existed, so it has no recorded quantities —
              only what was written down at the time.
            </p>
          ) : (
          <table className="lab-table batch-lines">
            {groupByPhase(lines.map((l, i) => ({ ...l, index: i }))).map(([phase, rows]) => (
              <tbody key={phase}>
                <tr className="phase-row">
                  <td colSpan={3}>{phase}</td>
                </tr>
                {rows.map((line) => (
                  <tr
                    key={line.index}
                    className={lineDone.includes(line.index) ? 'batch-line-done' : ''}
                  >
                    <td className="batch-check">
                      {!done && (
                        <input
                          type="checkbox"
                          checked={lineDone.includes(line.index)}
                          onChange={() => toggleLine(line.index)}
                          aria-label={`Weighed ${line.ingredient_name}`}
                        />
                      )}
                    </td>
                    <td>{line.ingredient_name}</td>
                    <td className="numeric strong">{formatGrams(line.grams)}</td>
                  </tr>
                ))}
              </tbody>
            ))}
            <tfoot>
              <tr className="lab-total ok">
                <td />
                <td>Total {formatPct(lines.reduce((s, l) => s + (Number(l.percentage) || 0), 0))}</td>
                <td className="numeric">
                  {formatBatchG(batch.planned_batch_g ?? batch.target_batch_g)}
                </td>
              </tr>
            </tfoot>
          </table>
          )}

          {lines.some((l) => l.phase) && <PhaseKey />}
        </section>

        <section className="panel lab-card">
          <div className="panel-head">
            <span className="panel-label">Method</span>
            {method.name && <span className="lab-row-meta">{method.name}</span>}
          </div>

          {steps.length === 0 ? (
            <p className="formula-muted">
              No method was attached when this batch was started, so there are no steps.
            </p>
          ) : (
            <ol className="batch-steps">
              {steps.map((step, i) => (
                <li
                  key={i}
                  className={
                    (stepDone.includes(i) ? 'done' : '') + (i === currentStep && !done ? ' current' : '')
                  }
                >
                  {!done && (
                    <input
                      type="checkbox"
                      checked={stepDone.includes(i)}
                      onChange={() => toggleStep(i)}
                      aria-label={`Step ${i + 1} done`}
                    />
                  )}
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          )}
        </section>
      </div>

      {done ? (
        <section className="panel lab-card">
          <div className="panel-head">
            <span className="panel-label">Result</span>
          </div>
          <div className="batch-result">
            <div>
              <span>Made</span>
              <strong>{batch.date_completed ? DATE.format(new Date(`${batch.date_completed}T12:00:00`)) : '—'}</strong>
            </div>
            <div>
              <span>Yield</span>
              <strong>
                {batch.actual_yield_units ?? '—'} of {planned || '—'}
              </strong>
              {yieldPercent(batch.actual_yield_units, planned) != null && (
                <em>{yieldPercent(batch.actual_yield_units, planned)}%</em>
              )}
            </div>
            <div>
              <span>Expires</span>
              <strong>
                {batch.expiry_date ? MONTH.format(new Date(`${batch.expiry_date}T12:00:00`)) : '—'}
              </strong>
            </div>
          </div>
          {batch.notes && <p className="batch-result-notes">{batch.notes}</p>}
          <div className="ingredient-form-actions">
            <button
              className="ghost-button"
              onClick={() => navigate(`/production?make=${batch.formula?.family_id}`)}
            >
              Repeat this batch
            </button>
          </div>
        </section>
      ) : (
        <div className="batch-footer">
          <button
            className="ghost-button"
            onClick={() =>
              changeStatus('abandoned', {
                title: 'Abandon this batch?',
                body: 'It stays in the log as abandoned, with everything it recorded. Nothing is deleted.',
              })
            }
          >
            Abandon
          </button>
          <button className="ghost-button" onClick={() => changeStatus('on_hold')}>
            Put on hold
          </button>
          {batch.status !== 'in_progress' && (
            <button className="ghost-button" onClick={() => changeStatus('in_progress')}>
              Start
            </button>
          )}
          <button className="primary-action" onClick={() => setCompleting(true)}>
            Mark complete
          </button>
        </div>
      )}

      {completing && (
        <CompleteBatchDialog batch={batch} onClose={() => setCompleting(false)} onSaved={load} />
      )}
    </>
  );
}
